import { useEffect, useRef } from 'react';

/**
 * Keeps the active Radix Tabs trigger scrolled into view inside a
 * horizontally-scrollable container.
 *
 * Usage:
 *   const scrollRef = useScrollActiveTabIntoView(activeTab);
 *   ...
 *   <div ref={scrollRef} className="overflow-x-auto">
 *     <TabsList>...</TabsList>
 *   </div>
 *
 * Why a custom scroll instead of `element.scrollIntoView`?
 *   The native method with `inline: 'center'` also scrolls the page
 *   vertically unless every ancestor is non-scrollable, which causes
 *   the whole dashboard to jump when a tab is clicked. This only
 *   adjusts horizontal scroll on the container itself, no vertical
 *   side-effects.
 */
export function useScrollActiveTabIntoView(activeValue) {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const active = container.querySelector('[data-state="active"][role="tab"]');
    if (!active) return;

    const containerWidth = container.clientWidth;
    const activeWidth = active.offsetWidth;
    const activeLeft = active.offsetLeft;
    // Centre the active tab when it fits, otherwise just reveal it.
    const target = Math.max(0, activeLeft - (containerWidth - activeWidth) / 2);
    container.scrollTo({ left: target, behavior: 'smooth' });
  }, [activeValue]);

  return containerRef;
}
